import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { ENOPOrganType, HubsCycleType, HubsViewMode } from '#shared';

import type { getMatchDetails } from '../helpers';
import type { RootState } from '../redux-store';

export interface ToolbarSettings {
  showHubs: boolean;
  hubsView: HubsViewMode;
  showAircraftsOnFly: boolean;
}

export type SearchResult = {
  label: string;
  organ?: ENOPOrganType;
  details: ReturnType<typeof getMatchDetails>;
};

// TODO move this to global default settings when available
const getDefaultHubsView = () => {
  if (window.matchMedia?.('(min-width: 3840px)').matches) {
    return HubsViewMode.Around;
  }
  return HubsViewMode.Flat;
};

export const DEFAULT_TOOLBAR: ToolbarSettings = {
  showHubs: true,
  showAircraftsOnFly: true,
  hubsView: getDefaultHubsView(),
};

export type ToolbarState = ToolbarSettings;

const initialState: ToolbarState = DEFAULT_TOOLBAR;

export const toolbarSlice = createSlice({
  name: 'toolbar',
  initialState,
  reducers: {
    // setters
    setShowHubs: (s, a: PayloadAction<boolean>) => {
      s.showHubs = a.payload;
    },
    setShowAircraftsOnFly: (s, a: PayloadAction<boolean>) => {
      s.showAircraftsOnFly = a.payload;
    },
    setHubsView: (s, a: PayloadAction<HubsViewMode>) => {
      s.hubsView = a.payload;
    },

    // toggles / cycles
    toggleShowAircraftsOnFly: s => {
      s.showAircraftsOnFly = !s.showAircraftsOnFly;
    },
    cycleHubsView: (s, a: PayloadAction<{ cycleType: HubsCycleType }>) => {
      const type = a?.payload?.cycleType ?? HubsCycleType.Base;
      const hv = s.hubsView;

      switch (type) {
        case HubsCycleType.Extended:
          if (hv === HubsViewMode.Regions) {
            s.hubsView = HubsViewMode.Flat;
          } else if (hv === HubsViewMode.Flat) {
            s.hubsView = HubsViewMode.Hidden;
          } else if (hv === HubsViewMode.Hidden) {
            s.hubsView = HubsViewMode.Around;
          } else {
            s.hubsView = HubsViewMode.Regions;
          }
          break;
        case HubsCycleType.Base:
          if (hv === HubsViewMode.Regions) {
            s.hubsView = HubsViewMode.Flat;
          } else if (hv === HubsViewMode.Flat) {
            s.hubsView = HubsViewMode.Hidden;
          } else {
            s.hubsView = HubsViewMode.Regions;
          }
          break;

        case HubsCycleType.Short:
          if (hv === HubsViewMode.Flat) {
            s.hubsView = HubsViewMode.Hidden;
          } else {
            s.hubsView = HubsViewMode.Flat;
          }
          break;
      }
    },

    // batch to update multiple settings at once
    updateMany: (s, a: PayloadAction<Partial<ToolbarSettings>>) => {
      Object.assign(s, a.payload);
    },

    // reset
    resetToolbar: () => ({ ...DEFAULT_TOOLBAR }),
  },
});

export const toolbarSelectors = {
  useSelectToolbar: () => useSelector((state: RootState) => state.toolbar),
  useSelectShowHubs: () =>
    useSelector((state: RootState) => state.toolbar.showHubs),
  useSelectShowAircraftsOnFly: () =>
    useSelector((state: RootState) => state.toolbar.showAircraftsOnFly),
  useSelectHubsView: () =>
    useSelector((state: RootState) => state.toolbar.hubsView),
};

export const toolbarReducer = toolbarSlice.reducer;
export const toolbarActions = toolbarSlice.actions;
